// Implements: F-UIX-024, F-UIX-007, F-UIX-009
/**
 * Which look styles a mockup and where it came from (F-DSG-011). Pure presentation.
 *
 * It shows the pin, never the profile's latest version nor the scope's active
 * profile now (P6-OD-20): the name (a link for a person who may read the
 * profile, the server's neutral label otherwise), the version and the level,
 * or that the system default applies. A newer version is announced and, for a
 * person who may re-pin, offered; nothing here re-pins by itself. The cascade
 * walked now sits in a collapsible panel whose open state is remembered by
 * `CollapsiblePanel`, which survives a storage that throws (checklist 15).
 */

import type { ReactElement } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { CollapsiblePanel } from "../../../shared/ui/organisms/CollapsiblePanel";
import type { MockupDesignView } from "../../../types/design";
import type { DesignKey } from "./editorOptions";
import styles from "./AppliedProfileIndicator.module.css";

const LEVELS: Readonly<Partial<Record<string, DesignKey>>> = {
  personal: "design.level.personal",
  team: "design.level.team",
  organization: "design.level.organization",
  system: "design.level.system",
};

const OUTCOMES: Readonly<Partial<Record<string, DesignKey>>> = {
  chosen: "design.outcome.chosen",
  no_active_profile: "design.outcome.noActiveProfile",
  archived: "design.outcome.archived",
  not_in_mockup_scope: "design.outcome.notInMockupScope",
  not_reached: "design.outcome.notReached",
  not_visible: "design.outcome.notVisible",
};

/** Where a readable profile opens: the profiles screen, on that profile. */
export function profileHref(profileId: string): string {
  return `/design/profiles?profile=${encodeURIComponent(profileId)}`;
}

export interface AppliedProfileIndicatorProps {
  readonly view: MockupDesignView;
  readonly onRepin: () => void;
  readonly pending: boolean;
  /** The server's refusal of the last re-pin. */
  readonly refusal?: string | undefined;
  /** The version the last re-pin moved the mockup to, announced once. */
  readonly repinnedVersion: number | null;
}

export function AppliedProfileIndicator({
  view,
  onRepin,
  pending,
  refusal,
  repinnedVersion,
}: AppliedProfileIndicatorProps): ReactElement {
  const { t } = useTranslation();
  const system = view.source_scope_kind === "system";
  const level = t(LEVELS[view.source_scope_kind] ?? "design.level.unknown");
  const offered = view.newer_version_available === true && view.latest_version !== null;

  return (
    <section className={styles.indicator} aria-labelledby="applied-design-title">
      <h2 id="applied-design-title" className={styles.title}>
        {t("design.indicator.title")}
      </h2>
      {system ? <p className={styles.line}>{t("design.indicator.systemDefault")}</p> : null}
      <p className={styles.line}>
        {view.profile_id === null ? (
          <span className={styles.name}>{view.profile_name}</span>
        ) : (
          <Link className={styles.name} to={profileHref(view.profile_id)}>
            {view.profile_name}
          </Link>
        )}
      </p>
      <p className={styles.line}>{`${t("design.indicator.version")} ${String(view.version)}`}</p>
      <p className={styles.line}>{`${t("design.indicator.level")}: ${level}`}</p>
      {offered ? (
        <p className={styles.line}>
          {`${t("design.indicator.newer")} ${String(view.latest_version)}`}
        </p>
      ) : null}
      {offered && view.may_repin ? (
        <Button variant="secondary" size="sm" loading={pending} disabled={pending} onClick={onRepin}>
          {t("design.indicator.repin")}
        </Button>
      ) : null}
      {repinnedVersion === null ? null : (
        <p role="status" className={styles.line}>
          {`${t("design.indicator.repinned")} ${String(repinnedVersion)}`}
        </p>
      )}
      {refusal === undefined ? null : (
        <p role="alert" className={styles.refusal}>
          {refusal}
        </p>
      )}
      <CollapsiblePanel id="design-cascade" title={t("design.indicator.cascade")}>
        <ul className={styles.trace}>
          {view.trace.map((step) => (
            <li key={step.rung}>
              {`${t(LEVELS[step.rung] ?? "design.level.unknown")}: ${t(OUTCOMES[step.outcome] ?? "design.outcome.unknown")}`}
            </li>
          ))}
        </ul>
      </CollapsiblePanel>
    </section>
  );
}
